import { useCallback, useContext, useState } from 'react';
import { Camelized } from 'humps';
import { AB_VARIANTS, useAB } from '@namespace/ab-test';
import {
  callGtmEvent,
  GTM_EVENT,
  LOG_ACTIONS,
  logAction,
  useLogAmplitude
} from '@namespace/analytics';
import { useGeneralRoutes, useOtherSettings } from '@namespace/cms';
import {
  getSessionStorageValue,
  SESSION_STORAGE_KEYS,
  setSessionStorageValue
} from '@namespace/helpers';
import { useI18n } from '@namespace/i18n';
import { useNotifications } from '@namespace/notifications';
import { useHistory, useNavigate } from '@namespace/router';
import { UserContext } from '@namespace/user';
import { DepositResponse } from '../../api/balance/types';
import { PAYMENT_SERVICE_IDS } from '../../constants';
import { RedirectMethods } from '../../constants/redirectMethods';
import { paymentsActions, STORE_PROPS, usePaymentsStore } from '../../store';
import { ApiError } from '../../types/apiError';
import { formatErrorMessage, formatSuccessMessage } from '../../utils';
import { useGetRecommendedDepositAmount } from '../useGetRecommendedDepositAmount';
import { useIsCurrencyUahAndNotVipUser } from '../useIsCurrencyUahAndNotVipUser';
import { useLastDepositAmount } from '../useLastDepositAmount';
import { useNativeDeposit } from '../useNativeDeposit';

const { DEPOSIT } = paymentsActions;

type OnDepositData = {
  amount?: string | number;
  paymentToken?: string;
  cardHash?: string;
  isNodePayments?: boolean;
  screenHeight?: number;
  screenWidth?: number;
  serviceId?: number;
  voucherId?: string;
};
export type OnDeposit = (
  data: OnDepositData,
  successMessage?: string,
  isDepositResultPage?: boolean
) => Promise<Camelized<DepositResponse> | null>;

export type OnDepositParams = Parameters<OnDeposit>;

export const useDeposit = () => {
  const { depositResult, successfulDeposit, unsuccessfulDeposit } =
    useGeneralRoutes();
  const { t, language } = useI18n();
  const { errorNotification, successNotification } = useNotifications();
  const [isFetching, setIsFetching] = useState(false);
  const { replace } = useHistory();
  const navigate = useNavigate();
  const activeServiceId = usePaymentsStore(STORE_PROPS.ACTIVE_SERVICE_ID);
  const [user] = useContext(UserContext);
  const { id } = user;

  const { getRecommendedDepositAmount } = useGetRecommendedDepositAmount();
  const { updateLastDepAmount } = useLastDepositAmount();
  const prevPage = getSessionStorageValue(
    SESSION_STORAGE_KEYS.PREV_PATHNAME,
    ''
  );
  const { isEnableDepositSourceURL = false } = useOtherSettings();

  const variant = useAB(AB_VARIANTS.PRESET_DEPOSIT_UAH);
  const isCurrencyUahAndNotVipUser = useIsCurrencyUahAndNotVipUser();
  const { onDeposit: onNativeDeposit } = useNativeDeposit();
  const logAmplitude = useLogAmplitude();

  const onDeposit: OnDeposit = useCallback(
    async (data, successMessage = '', isDepositResultPage = true) => {
      const { isNodePayments } = data;
      try {
        setIsFetching(true);
        const isNativeFlow = PAYMENT_SERVICE_IDS.APPLE_PAY_DIRECT.includes(
          activeServiceId as number
        );

        const isAbon = PAYMENT_SERVICE_IDS.ABON.includes(
          activeServiceId as number
        );
        const paymentTokenType = isAbon ? 'couponCode' : 'paymentToken';

        const deposit = isNativeFlow ? onNativeDeposit : DEPOSIT;

        const result = (await deposit({
          amount: data.amount as string,
          [paymentTokenType]: data.paymentToken,
          cardHash: data.cardHash,
          serviceId: activeServiceId,
          lang: language,
          userId: id,
          isNodePayments: isNodePayments as boolean,
          screenHeight: data?.screenHeight,
          screenWidth: data?.screenWidth,
          voucherId: data?.voucherId
        })) as Camelized<DepositResponse>;

        if (data.amount) {
          updateLastDepAmount(data.amount);
        }
        getRecommendedDepositAmount();

        callGtmEvent(GTM_EVENT.DEPOSIT, {
          event_category: 'Account',
          event_label: `Deposit_${activeServiceId}`
        });
        logAction(LOG_ACTIONS.DEPOSIT, {});
        setSessionStorageValue(SESSION_STORAGE_KEYS.FROM_DEPOSIT, 1);
        setSessionStorageValue(SESSION_STORAGE_KEYS.DEPOSIT_DATA, {
          amount: data.amount,
          serviceId: activeServiceId,
          userId: id
        });
        logAmplitude({
          actionName: LOG_ACTIONS.DEPOSIT_CLICK_DEPOSIT_BTN,
          attributes: {
            ...(data.amount && {
              amount: data.amount
            })
          }
        });

        if (
          isEnableDepositSourceURL &&
          ![successfulDeposit, unsuccessfulDeposit].some((page) =>
            prevPage?.includes(page)
          )
        ) {
          setSessionStorageValue(
            SESSION_STORAGE_KEYS.PREV_PAGE_BEFORE_DEPOSIT,
            prevPage
          );
        }

        if (variant && isCurrencyUahAndNotVipUser) {
          logAction(LOG_ACTIONS.AB_TEST_PRESET_DEPOSIT_UAH, {
            userId: id,
            amount: data.amount
          });
        }

        if (successMessage) {
          successNotification(t(formatSuccessMessage(result, successMessage)));
        }
        if (isDepositResultPage) {
          const { redirectMethod } = result;

          if (
            redirectMethod === RedirectMethods.Redirect3ds ||
            redirectMethod === RedirectMethods.RedirectUrl
          ) {
            navigate(depositResult);
          } else {
            replace(depositResult);
          }
        }
        if (isAbon && !isDepositResultPage) {
          replace(successfulDeposit);
        }

        return result as Camelized<DepositResponse>;
      } catch (response) {
        const { errorCode } = response as ApiError;
        errorNotification(t(formatErrorMessage(errorCode)));
      } finally {
        setIsFetching(false);
      }
      return null;
    },
    [
      activeServiceId,
      onNativeDeposit,
      language,
      id,
      updateLastDepAmount,
      getRecommendedDepositAmount,
      logAmplitude,
      isEnableDepositSourceURL,
      successfulDeposit,
      unsuccessfulDeposit,
      variant,
      isCurrencyUahAndNotVipUser,
      prevPage,
      successNotification,
      t,
      replace,
      depositResult,
      errorNotification
    ]
  );

  return {
    onDeposit,
    isFetching
  };
};
